import { useCallback, useState } from "react";
import { useLocation } from "react-router-dom";
import type { Patient, Treatment, Session } from "@akina/domain";
import { useGetDeviceInfo, useGetOSInfo } from "@akina/presentation";
import {
  type PropertyTypeMap,
  type SessionPropertyTypeMap,
  AmplitudeProperties,
  type ScreenStateType,
  SessionAmplitudeProperties,
  SessionScreenTypes,
} from "./";

export const calculateScreenDuration = (startTime: number | null) => {
  if (startTime) return (Date.now() - startTime) / 1000;
  return null;
};

export const getScreenDurationProperty = (screenStartTime: number | null) => ({
  [SessionAmplitudeProperties.ScreenDurationAtExit]: calculateScreenDuration(screenStartTime),
});

export const addScreenTime = (analyticsData: SessionPropertyTypeMap) => {
  const startTime = analyticsData[SessionAmplitudeProperties.ScreenDurationAtExit];
  return {
    ...analyticsData,
    ...getScreenDurationProperty(startTime),
  };
};

export const addCameraSwitchData = (webcamIsFullScreen: boolean, screenType: SessionScreenTypes) => {
  const screensToTrack = [SessionScreenTypes.RoutineScreen, SessionScreenTypes.BreakScreen];
  if (!screensToTrack.includes(screenType)) return {};
  return {
    [SessionAmplitudeProperties.ScreenState]: webcamIsFullScreen
      ? "camera_in_center"
      : ("coach_in_center" as ScreenStateType),
  };
};

export const getSessionEventInfo = ({
  screenType,
  screenIndex,
  session,
  screenDurationAtExit,
}: {
  screenType: string;
  screenIndex: number;
  session?: Session | null;
  screenDurationAtExit?: number | null;
}): SessionPropertyTypeMap => ({
  [SessionAmplitudeProperties.CurrentPhaseWeek]: session?.week ?? 0,
  [SessionAmplitudeProperties.ScreenType]: screenType,
  [SessionAmplitudeProperties.ScreenIndex]: screenIndex,
  [SessionAmplitudeProperties.IsInitialSession]: session ? session?.currentNumber == 1 : true,
  [SessionAmplitudeProperties.ScreenDurationAtExit]: screenDurationAtExit ?? Date.now(),
  [SessionAmplitudeProperties.SessionLogId]: session?.id ?? "",
  [SessionAmplitudeProperties.SessionLogIndex]: session?.currentNumber ?? 0,
  [SessionAmplitudeProperties.ScreenState]: "coach_in_center",
});

export const useTrackSessionEventInfo = ({
  screenType,
  screenIndex,
  session,
}: {
  screenType: string;
  screenIndex: number;
  session?: Session | null;
}) => {
  const [screenDurationAtExit, setScreenDurationAtExit] = useState<number | null>(() => Date.now());

  const getData = useCallback(
    (): SessionPropertyTypeMap =>
      getSessionEventInfo({
        screenType,
        screenIndex,
        session,
        screenDurationAtExit,
      }),
    [screenType, screenIndex, session, screenDurationAtExit],
  );

  const resetScreenDurationAtExit = useCallback(() => {
    setScreenDurationAtExit(Date.now());
  }, []);

  const addSessionData = useCallback(
    (session: Session | undefined, analyticsData: SessionPropertyTypeMap) => ({
      ...analyticsData,
      [SessionAmplitudeProperties.CurrentPhaseWeek]: session?.week ?? 0,
      [SessionAmplitudeProperties.IsInitialSession]: session?.currentNumber == 1,
      [SessionAmplitudeProperties.SessionLogId]: session?.id ?? "",
      [SessionAmplitudeProperties.SessionLogIndex]: session?.currentNumber ?? 0,
    }),
    [],
  );

  const data = getData();

  return { data, getData, addSessionData, resetScreenDurationAtExit };
};

export const getCareEventInfo = ({
  pageName,
  pathname,
  parsedOsInfo,
  parsedDeviceInfo,
}: {
  pageName: string;
  pathname: string;
  parsedOsInfo: string;
  parsedDeviceInfo: string;
}): PropertyTypeMap => ({
  [AmplitudeProperties.Timestamp]: Date.now(),
  [AmplitudeProperties.UserId]: "",
  [AmplitudeProperties.Language]: "",
  [AmplitudeProperties.BrowserInformation]: navigator.userAgent,
  [AmplitudeProperties.App]: "akina-care",
  [AmplitudeProperties.Height]: window.innerHeight,
  [AmplitudeProperties.Width]: window.innerWidth,
  [AmplitudeProperties.PageName]: pageName,
  [AmplitudeProperties.Path]: pathname,
  [AmplitudeProperties.TreatmentId]: "",
  [AmplitudeProperties.TreatmentTemplateKey]: "",
  [AmplitudeProperties.OperatingSystemInformation]: parsedOsInfo,
  [AmplitudeProperties.DeviceInformation]: parsedDeviceInfo,
  [AmplitudeProperties.ScreenResolution]: `${window.innerWidth}x${window.innerHeight}`,
  [AmplitudeProperties.OrganizationId]: "",
  [AmplitudeProperties.BatteryLevel]: 0,
  [AmplitudeProperties.BatteryAvailable]: false,
  [AmplitudeProperties.BatteryCharging]: false,
  [AmplitudeProperties.BatteryHealth]: "healthy",
  [AmplitudeProperties.GpuIntegrated]: "",
  [AmplitudeProperties.GpuDiscrete]: "",
  [AmplitudeProperties.GpuCpu]: "",
  [AmplitudeProperties.GpuAvailable]: false,
  [AmplitudeProperties.GpuHealth]: "healthy",
});

export const addPatientAndTreatmentData = (
  patient: Patient | undefined,
  treatment: Treatment | undefined | null,
  analyticsData: PropertyTypeMap,
): PropertyTypeMap => ({
  ...analyticsData,
  [AmplitudeProperties.OrganizationId]: treatment?.clinicId ?? "",
  [AmplitudeProperties.UserId]: patient?.patientId || "",
  [AmplitudeProperties.Language]: patient?.preferences.language || "",
  [AmplitudeProperties.TreatmentId]: treatment?.id || patient?.treatmentIds[0] || "",
  [AmplitudeProperties.TreatmentTemplateKey]: treatment?.treatmentTemplateKey || "",
});

export const useTrackCareEventInfo = ({ pageName }: { pageName: string }) => {
  const { pathname } = useLocation();
  const { parsedOsInfo } = useGetOSInfo();
  const { parsedDeviceInfo } = useGetDeviceInfo();

  const getData = useCallback(
    (): PropertyTypeMap =>
      getCareEventInfo({
        pageName,
        pathname,
        parsedOsInfo,
        parsedDeviceInfo,
      }),
    [pageName, pathname, parsedOsInfo, parsedDeviceInfo],
  );

  const data = getData();

  return {
    data,
    getData,
    addPatientAndTreatmentData,
  };
};
